import type {
  CardSpec,
  Constants,
  CrownMode,
  MatchConfig,
  MatchMode,
  PawnSpec,
  PresetId,
  ReserveSpec,
  Seating,
  Side,
} from './types';
import { isCardNumber, isColor } from './cards';
import { ledgerProblems } from './ledger/validate';

// The version constants live in a module without imports, so vite.config.ts can read them too.
export { REPLAY_FORMAT_VERSION, RULES_VERSION } from './version';

/** Tunable constants, spec §22. Every rule reads these from the match config. */
export const DEFAULT_CONSTANTS: Readonly<Constants> = Object.freeze({
  DECK_COPIES_PER_FACE: 2,
  WILDS_PER_DECK: 2,
  HAND_START: 7,
  HAND_LIMIT: 7,
  MAX_LINKS: 3,
  POINTS_LANDING: 1,
  POINTS_CAPTURE: 5,
  POINTS_CROWN: 8,
  BONUS_ECHO: 1,
  BONUS_STREAK: 1,
  BONUS_LADDER: 2,
  BONUS_FULL_CIRCLE: 2,
  EPITAPH_SLOTS: 1,
  EPITAPH_PLAYS_PER_ROUND: 1,
  MAX_ROUNDS: 3,
  ROUND_WINS_NEEDED: 2,
  DEFAULT_PRESET: 'A',
  CAMPAIGN_TABLES: 3,
  CAMPAIGN_HEAD_START: [0, 20, 50],
  CAMPAIGN_STARTING_STABLE: 6,
  CAMPAIGN_FIELDED: 6,
  CAMPAIGN_RECRUITS_PER_WIN: 1,
  CAMPAIGN_OFFERS: 3,
  CAMPAIGN_OFFER_POOL: 'faces+wild',
  CAMPAIGN_FORMER_PAWNS_MAX: 2,
  AI_DECISION_BUDGET_MS: 2000,
} satisfies Constants);

export interface PresetDefinition {
  readonly id: Exclude<PresetId, 'CUSTOM'>;
  readonly homeRowImmunity: boolean;
  readonly crownMode: CrownMode;
}

/** Rule presets, spec §13. */
export const PRESETS: Readonly<Record<Exclude<PresetId, 'CUSTOM'>, PresetDefinition>> =
  Object.freeze({
    A: { id: 'A', homeRowImmunity: true, crownMode: 'immediate' },
    B: { id: 'B', homeRowImmunity: false, crownMode: 'delayed' },
    BASELINE: { id: 'BASELINE', homeRowImmunity: false, crownMode: 'immediate' },
  });

export interface ConfigOptions {
  mode?: MatchMode;
  /** Official preset; defaults to constants.DEFAULT_PRESET. */
  preset?: PresetId;
  /** Only for preset 'CUSTOM' (internal experiments, §13). */
  homeRowImmunity?: boolean;
  crownMode?: CrownMode;
  northHeadStart?: number;
  constants?: Partial<Constants>;
  seating?: Seating;
  decks?: Partial<Record<Side, readonly CardSpec[]>>;
  pawns?: Partial<Record<Side, readonly PawnSpec[]>>;
  reserves?: Partial<Record<Side, readonly ReserveSpec[]>>;
  /** The Long Night ruleset (LEDGER.MD). */
  ledger?: MatchConfig['ledger'];
}

/** Builds a complete, validated match config. Throws on invalid input. */
export function makeConfig(options: ConfigOptions = {}): MatchConfig {
  const constants: Constants = {
    ...DEFAULT_CONSTANTS,
    CAMPAIGN_HEAD_START: [...DEFAULT_CONSTANTS.CAMPAIGN_HEAD_START],
    ...options.constants,
  };
  const preset = options.preset ?? constants.DEFAULT_PRESET;
  let homeRowImmunity: boolean;
  let crownMode: CrownMode;
  if (preset === 'CUSTOM') {
    homeRowImmunity = options.homeRowImmunity ?? true;
    crownMode = options.crownMode ?? 'immediate';
  } else {
    const def = PRESETS[preset];
    if (!def) throw new Error(`Unknown preset: ${String(preset)}`);
    homeRowImmunity = def.homeRowImmunity;
    crownMode = def.crownMode;
  }
  const mode = options.mode ?? 'duel';
  const config: MatchConfig = {
    mode,
    preset,
    homeRowImmunity,
    crownMode,
    northHeadStart: options.northHeadStart ?? 0,
    constants,
    seating: options.seating ?? { players: ['player-1', 'player-2'], assignment: 'random' },
    ...(options.decks ? { decks: options.decks } : {}),
    ...(options.pawns ? { pawns: options.pawns } : {}),
    ...(options.reserves ? { reserves: options.reserves } : {}),
    ...(options.ledger ? { ledger: options.ledger } : {}),
  };
  const problems = validateConfig(config);
  if (problems.length > 0) throw new Error(`Invalid match config: ${problems.join('; ')}`);
  return config;
}

const isNonNegInt = (n: unknown): n is number =>
  typeof n === 'number' && Number.isInteger(n) && n >= 0;
const isPosInt = (n: unknown): n is number => isNonNegInt(n) && n > 0;

/**
 * Upper bounds for numeric constants. They sit far above any playable tuning and only keep an
 * untrusted config (an imported replay, a stored save) from exhausting memory or time.
 */
const SANITY_LIMITS: Partial<Record<keyof Constants, number>> = {
  DECK_COPIES_PER_FACE: 10,
  WILDS_PER_DECK: 20,
  HAND_START: 20,
  HAND_LIMIT: 20,
  MAX_LINKS: 10,
  POINTS_LANDING: 1000,
  POINTS_CAPTURE: 1000,
  POINTS_CROWN: 1000,
  BONUS_ECHO: 100,
  BONUS_STREAK: 100,
  BONUS_LADDER: 100,
  BONUS_FULL_CIRCLE: 100,
  EPITAPH_SLOTS: 5,
  EPITAPH_PLAYS_PER_ROUND: 5,
  MAX_ROUNDS: 9,
  ROUND_WINS_NEEDED: 9,
  CAMPAIGN_TABLES: 10,
  CAMPAIGN_STARTING_STABLE: 50,
  CAMPAIGN_FIELDED: 6,
  CAMPAIGN_RECRUITS_PER_WIN: 10,
  CAMPAIGN_OFFERS: 13,
  CAMPAIGN_FORMER_PAWNS_MAX: 6,
  AI_DECISION_BUDGET_MS: 60_000,
};
/** Most cards a side's deck may hold, and most reserves a side may bring. */
const MAX_DECK_SIZE = 200;
const MAX_RESERVES = 50;
const MAX_HEAD_START = 100_000;
/** Long Night head starts can grow far beyond a duel's (docs/SHOP_DESIGN.md §8.5). */
const MAX_LEDGER_HEAD_START = 1e15;

/** Returns a list of problems; empty when the config is valid. */
export function validateConfig(config: MatchConfig): string[] {
  const problems: string[] = [];
  const c = config.constants;
  if (!c || typeof c !== 'object') return ['constants missing'];
  if (config.mode !== 'duel' && config.mode !== 'campaign')
    problems.push('mode must be duel or campaign');
  if (config.preset !== 'CUSTOM') {
    const def = PRESETS[config.preset];
    if (!def) problems.push(`unknown preset ${String(config.preset)}`);
    else if (def.homeRowImmunity !== config.homeRowImmunity || def.crownMode !== config.crownMode)
      problems.push(`preset ${config.preset} does not match its rule switches`);
  }
  if (config.crownMode !== 'immediate' && config.crownMode !== 'delayed')
    problems.push('bad crownMode');
  if (typeof config.homeRowImmunity !== 'boolean') problems.push('bad homeRowImmunity');
  const headCap = config.ledger ? MAX_LEDGER_HEAD_START : MAX_HEAD_START;
  if (!isNonNegInt(config.northHeadStart) || config.northHeadStart > headCap)
    problems.push(`northHeadStart must be an integer from 0 to ${headCap}`);
  if (config.mode === 'duel' && config.northHeadStart !== 0)
    problems.push('duels have no head start');

  for (const key of [
    'DECK_COPIES_PER_FACE',
    'WILDS_PER_DECK',
    'POINTS_LANDING',
    'POINTS_CAPTURE',
    'POINTS_CROWN',
    'BONUS_ECHO',
    'BONUS_STREAK',
    'BONUS_LADDER',
    'BONUS_FULL_CIRCLE',
    'CAMPAIGN_FORMER_PAWNS_MAX',
    'CAMPAIGN_RECRUITS_PER_WIN',
    'CAMPAIGN_OFFERS',
  ] as const) {
    if (!isNonNegInt(c[key])) problems.push(`${key} must be a non-negative integer`);
  }
  for (const key of [
    'HAND_START',
    'HAND_LIMIT',
    'MAX_LINKS',
    'EPITAPH_SLOTS',
    'EPITAPH_PLAYS_PER_ROUND',
    'MAX_ROUNDS',
    'ROUND_WINS_NEEDED',
    'CAMPAIGN_TABLES',
    'CAMPAIGN_STARTING_STABLE',
    'CAMPAIGN_FIELDED',
    'AI_DECISION_BUDGET_MS',
  ] as const) {
    if (!isPosInt(c[key])) problems.push(`${key} must be a positive integer`);
  }
  for (const [key, max] of Object.entries(SANITY_LIMITS) as [keyof Constants, number][]) {
    const value = c[key];
    if (typeof value === 'number' && value > max) problems.push(`${key} cannot exceed ${max}`);
  }
  if (isPosInt(c.HAND_START) && isPosInt(c.HAND_LIMIT) && c.HAND_START > c.HAND_LIMIT)
    problems.push('HAND_START cannot exceed HAND_LIMIT');
  if (isPosInt(c.ROUND_WINS_NEEDED) && isPosInt(c.MAX_ROUNDS) && c.ROUND_WINS_NEEDED > c.MAX_ROUNDS)
    problems.push('ROUND_WINS_NEEDED cannot exceed MAX_ROUNDS');
  if (
    !Array.isArray(c.CAMPAIGN_HEAD_START) ||
    c.CAMPAIGN_HEAD_START.length > 20 ||
    !c.CAMPAIGN_HEAD_START.every((h) => isNonNegInt(h) && h <= MAX_HEAD_START)
  )
    problems.push('CAMPAIGN_HEAD_START must be a short list of non-negative integers');
  if (c.DEFAULT_PRESET !== 'A' && c.DEFAULT_PRESET !== 'B' && c.DEFAULT_PRESET !== 'BASELINE')
    problems.push('DEFAULT_PRESET must be A, B or BASELINE');

  const seating = config.seating;
  if (
    !seating ||
    !Array.isArray(seating.players) ||
    seating.players.length !== 2 ||
    !seating.players.every((p) => typeof p === 'string' && p.length > 0) ||
    seating.players[0] === seating.players[1]
  )
    problems.push('seating.players must be two distinct non-empty ids');
  if (seating && seating.assignment !== 'random' && seating.assignment !== 'fixed')
    problems.push('seating.assignment must be random or fixed');

  const ids = new Set<string>();
  for (const side of ['S', 'N'] as const) {
    const deck = config.decks?.[side];
    if (deck !== undefined) {
      if (!Array.isArray(deck) || deck.length === 0 || deck.length > MAX_DECK_SIZE)
        problems.push(`deck ${side} must be a list of 1 to ${MAX_DECK_SIZE} cards`);
      else
        for (const spec of deck as unknown[] as (CardSpec | null)[]) {
          if (typeof spec !== 'object' || spec === null) {
            problems.push(`deck ${side} holds an invalid card`);
            continue;
          }
          if (
            !config.ledger &&
            (spec.stamp !== undefined ||
              spec.finish !== undefined ||
              spec.value !== undefined ||
              spec.trick !== undefined)
          )
            problems.push(`deck ${side}: Stamps, Finishes and Tricks belong to the Long Night`);
          if (spec.kind === 'wild') continue;
          if (
            spec.kind !== 'normal' ||
            !isColor(spec.face?.color) ||
            !isCardNumber(spec.face?.number)
          )
            problems.push(`deck ${side} holds an invalid card`);
        }
    }
    const pawns = config.pawns?.[side];
    if (pawns !== undefined) {
      if (!Array.isArray(pawns) || pawns.length !== 6)
        problems.push(`side ${side} needs exactly six pawns`);
      else {
        const files = new Set<number>();
        for (const p of pawns as unknown[] as (PawnSpec | null)[]) {
          if (typeof p !== 'object' || p === null) {
            problems.push(`side ${side} holds an invalid pawn`);
            continue;
          }
          if (typeof p.id !== 'string' || p.id.length === 0)
            problems.push('pawn ids must be non-empty');
          if (ids.has(p.id)) problems.push(`duplicate pawn id ${p.id}`);
          ids.add(p.id);
          if (!Number.isInteger(p.file) || p.file < 0 || p.file > 5)
            problems.push(`bad file for pawn ${p.id}`);
          if (p.carvings !== undefined && !config.ledger)
            problems.push(`pawn ${p.id}: Carvings belong to the Long Night`);
          files.add(p.file);
        }
        if (files.size !== 6) problems.push(`side ${side} pawns must start on six different files`);
      }
    }
  }
  for (const side of ['S', 'N'] as const) {
    if (config.pawns?.[side] === undefined)
      for (const f of 'ABCDEF') {
        const id = `${side}-${f}`;
        if (ids.has(id)) problems.push(`duplicate pawn id ${id}`);
        ids.add(id);
      }
  }
  for (const side of ['S', 'N'] as const) {
    const reserves = config.reserves?.[side];
    if (reserves === undefined) continue;
    if (!Array.isArray(reserves) || reserves.length > MAX_RESERVES) {
      problems.push(`reserves ${side} must be a list of at most ${MAX_RESERVES} pawns`);
      continue;
    }
    for (const r of reserves as unknown[] as (ReserveSpec | null)[]) {
      if (typeof r !== 'object' || r === null) {
        problems.push(`reserves ${side} hold an invalid pawn`);
        continue;
      }
      if (typeof r.id !== 'string' || r.id.length === 0)
        problems.push('reserve ids must be non-empty');
      if (ids.has(r.id)) problems.push(`duplicate pawn id ${r.id}`);
      ids.add(r.id);
    }
  }
  if (config.ledger !== undefined) problems.push(...ledgerProblems(config));
  return problems;
}
